<template>
    <div>
        <div v-for="header in headers" :key="header.title">
            <v-card-title>{{ header.title }}</v-card-title>
            <v-card-text class="body-1" v-if="header.body">{{
                header.body
            }}</v-card-text>
            <component
                v-if="header.comp"
                :is="header.comp.name"
                v-bind="{ ...header.comp.props }"
                :class="header.comp.classes"
            />
        </div>
        <v-card-title>Musicologie Junior FAQ:</v-card-title>
        <v-expansion-panels v-model="panels" class="soli-round" accordion>
            <v-expansion-panel v-for="FAQ in FAQS" :key="FAQ.question">
                <v-expansion-panel-header class="font-weight-bold">{{
                    FAQ.question
                }}</v-expansion-panel-header>
                <v-expansion-panel-content
                    ><div
                        v-if="FAQ.answer"
                        v-html="FAQ.answer"
                        class="body-1" />
                    <component
                        v-if="FAQ.comp"
                        :is="FAQ.comp.name"
                        v-bind="{ ...FAQ.comp.props }"
                        :class="FAQ.comp.classes"
                /></v-expansion-panel-content>
            </v-expansion-panel>
        </v-expansion-panels>
    </div>
</template>

<script>
import IndentList from "./IndentList.vue";
import RenderList from "./RenderList.vue";

export default {
    components: {
        RenderList,
        IndentList,
    },
    props: {
        location: {
            type: Object,
            default: () => {},
        },
    },
    data() {
        return {
            panels: null,
            locationPrices: [],
        };
    },
    computed: {
        headers() {
            return [
                {
                    title: "Musicologie Junior",
                    body: `Experience Simply Delightful Music Lessons with Musicologie! We are a premier music education company for ages 6 months through adults. Our programs are specifically designed to make learning and playing music fun for all ages, abilities and instruments.`,
                },
                {
                    title: "Our Approach to Music Lessons for Toddlers",
                    body: `At Musicologie we know that music lessons are one of the most impactful investments you can make in your child. Learning music improves coordination, language, reading and math skills. It nurtures our emotional and cognitive health. Studying music literally makes our brains bigger. We designed Musicologie Junior to be a fun and easy way for your little one to interact with others and meet new friends in a stimulating educational environment.
                    `,
                },
            ];
        },
        FAQS() {
            return [
                {
                    question: "What is Musicologie Junior?",
                    answer: `Musicologie Junior is a musical adventure for toddlers and their caregivers. At each class, we’ll sing songs, move our bodies, dance to different styles of music, read sing-along books, and play lots of musical instruments while your child reinforces her naturally-developing senses of rhythm, melody, rhyme, speech, and movement! We incorporate methods from Kodaly, Dalcroze, and Orff.`,
                },
                {
                    question: "How does Musicologie Junior work?",
                    answer: `Musicologie Junior is a 40 minute experiential music class for ages 6 months to five years. Classes will reinforce your child’s naturally growing musical and developmental skills. Each class follows a set format with nine sections, exploring different elements of music making.`,
                    comp: {
                        name: "IndentList",
                        classes: ["px-4"],
                        props: {
                            numberedList: true,
                            items: [
                                "1. Hello We sing the hello song to all our musical friends and get ready to make music together.",
                                "2. Circle Time We explore melodies and rhythms with hands and with rhythm instruments.",
                                "3. Melody of the Week We learn a melodic pattern using solfege (Do, Re, Mi) and a musical sign language called Curwen Hand Signs.",
                                "4. Rhythm of the Week We learn a rhythmic symbol and pattern using music notation and Kodaly-inspired rhythm syllables (Ta Ti-Ti).",
                                "5. Instrument of the Week We explore an instrument, learn how it works and build comfort and confidence exploring real musical instruments.",
                                "6. Free Play We have a jam session to a wide variety of music styles and genres with lots of different rhythmic and melodic instruments. This is a fun and easy way to introduce your child to new, exciting sounds.",
                                "7. Movement We stand up and feel the music with movement and the entire body with Dalcroze-inspired exploration.",
                                "8. Lullaby We feel the heartbeat rhythm of a lullaby as we sing a stuffed animal baby to sleep.",
                                "9. Goodbye We sing goodbye to all our music friends and get ready to take all these songs home and explore more during the week with our weekly activities.",
                            ],
                        },
                    },
                },
                {
                    question: "Who is Musicologie Junior for?",
                    answer: `Musicologie Junior is for kids age 6 months - 5 years old and their caregivers. It's for families who want to meet others and give their child the opportunity to explore in an engaging, educational environment full of music and fun.`,
                },
                {
                    question: "What musical concepts will my little one learn?",
                    comp: {
                        name: "IndentList",
                        classes: ["px-4"],
                        props: {
                            items: [
                                `<b>6-18 months</>`,
                                "· Comfort in a group setting",
                                "· Exposure to and confidence exploring instruments",
                                "· Exposure to music from many cultures, in different languages, and from a variety of genres",
                                "· Observing and listening to others making music",
                                "· Side-by-side or guided participation with an adult in songs and movements",
                                "· Vocalizing sounds or words during songs or in response to songs",
                                "· Feeling a heartbeat rhythm - the steady pulse of the music",
                                "· Exploring gross motor movements in response to music",
                                "· Expanding range of melodies and rhythms heard and experienced",
                                "· Connecting musical concepts with physical movements: high/low, fast/slow, forte/piano",
                                `<br/>`,
                                "<b>18 months - 3 years<br/>",
                                "· Comfort in a group setting",
                                "· Confidence exploring instruments and connecting musical concepts to that exploration Exposure to music from many cultures, in different languages, and from a variety of genres Growing sense of self as music-creator Increasing independence from adult in songs and movements",
                                "· Vocalizing sounds or words during songs and in response to songs Increasing sense of heartbeat rhythm",
                                "· Connecting gross motor movements in response to rhythmic and melodic changes Growing confidence in improvisation and music creation",
                                "· Developing an understanding of Curwen Musical Sign Language Developing understanding of basic musical concepts and vocabulary: high/low, fast/slow, forte/piano Beginning to perceive and repeat pitches, melodies, and basic rhythms",
                                "· Building readiness skills for school and private lessons: independent play, expressing emotions, planning and thinking ahead, body awareness, working memory, positive personal identity",
                                "<br/>",
                                "<b>3 - 5 years</b>",
                                "· Confidence exploring and holding instruments",
                                "· Connecting musical concepts and vocabulary to instrument exploration",
                                "· Exposure to music from many cultures, in different languages, and from a variety of genres",
                                "· Growing sense of self as a music-creator Independence from adult in songs and movements Singing known songs with increasing confidence",
                                "· Growing confidence when faced with new songs, activities, and instruments Increasing sense of heartbeat rhythm Increasing sense of melodic rhythm",
                                "· Connecting gross and fine motor movements in response to rhythmic and melodic changes Listening to and repeating pitches, melodies, and rhythms",
                                "· Confidence in improvisation and creativity A beginning understanding of Curwen Musical Sign Language",
                                "· A beginning understanding of simple Solfege patterns and melodies",
                                "· A beginning understanding of simple rhythmic patterns",
                                "· A beginning understanding of basic musical concepts and vocabulary: high/low, fast/slow, forte/piano",
                                "· Building readiness skills for school and private music lessons: independent play, expressing emotions, planning and thinking ahead, body awareness, working memory, tracking left to right, positive personal identity",
                            ],
                        },
                    },
                },
                {
                    question: "How do I get started?",
                    answer: `Contact us to register for your first class free. Classes occur every week – there are no semesters or terms. Classes are mixed age and introduce repeated themes and songs. There are no prerequisites, the class is designed for you and your little one to jump right in and participate.`,
                },
                {
                    hidden: !this.location,
                    question: "What’s the pricing?",
                    answer: `Musicologie Junior is ${this.getPrice(
                        "Junior Membership"
                    )} per month at Musicologie ${
                        this.location?.name
                    }, billed on the 1st of each month. Your first class is free!`,
                },
                {
                    question: "Is there a sibling or family discount?",
                    answer: `Yes. There is a 3% discount off your entire invoice if more than one family member is taking lessons. The discount is applied automatically and you'll see it on your monthly invoice.`,
                },
                {
                    question: "What’s Musicologie’s withdrawal policy?",
                    answer: `To withdraw from Musicologie and end automatic billing, all you need to do is fill out the quick form at https://musicologie.com/exit 30 days before your last class. Your withdrawal is not processed until you fill out that form. We require 30 days notice, which begins the day that form is received. Your final invoice will be prorated the last 30 days.`,
                },
                {
                    question: "What’s Musicologie’s class cancellation policy?",
                    answer: `You can cancel or change your class up to 30 minutes before your class time. If you cancel, that class stays on your account and you can reschedule it at any time in the future. Canceled classes never expire as long as you’re a current student.`,
                },
                {
                    question: "How do I reschedule my class?",
                    answer: `Log into your account at Musicologie.com to view your schedule and select any class to cancel or change. You can cancel or change up to 30 minutes before your lesson.`,
                },
            ].filter((e) => !e.hidden);
        },
    },
    methods: {
        getPrice(product) {
            if (!this.locationPrices.length) return "(...loading)";
            return this.formatUnitAmount(
                this.locationPrices.find((e) => e.name == product).amount
            );
        },
        getLocationPricing() {
            if (!this.location) return;

            axios
                .get(route("stripe.locationPricing"), {
                    params: {
                        location_id: this.location.id,
                    },
                })
                .then((resp) => {
                    this.locationPrices = resp.data;
                });
        },
    },
    mounted() {
        this.getLocationPricing();
    },
};
</script>
